import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, User, Users, Phone, ChevronDown, CreditCard, RefreshCw, DollarSign } from 'lucide-react';
import { useSession } from '../../providers/SessionProvider';
import { clientsApi } from '../../api/clients.api';

// Payment method icons
const ZelleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#6C1ED9">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z" />
    </svg>
);

// Stripe icon
const StripeIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#6772E5">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3 11h-2v2c0 .55-.45 1-1 1s-1-.45-1-1v-2H9c-.55 0-1-.45-1-1s.45-1 1-1h2V9c0-.55.45-1 1-1s1 .45 1 1v2h2c.55 0 1 .45 1 1s-.45 1-1 1z" />
    </svg>
);

// PayPal icon
const PaypalIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0070BA">
        <path d="M9.112 18.578H5.755a.527.527 0 01-.52-.601L7.19 4.045a.634.634 0 01.626-.535h5.056c1.673 0 2.997.377 3.935 1.121.957.761 1.434 1.88 1.417 3.325-.006.048-.006.096-.011.144-.206 1.322-.75 2.361-1.617 3.087-.934.785-2.222 1.182-3.834 1.182h-1.493a.791.791 0 00-.781.668l-.615 3.899a.527.527 0 01-.52.601h-.241v.041z" />
    </svg>
);

interface Compte {
    id: string;
    code: string;
    type: string;
    devise: string;
    solde?: number;
    soldeBS?: number;
    solde_BS?: number;
}

interface PaymentMethod {
    id: string;
    name: string;
    icon: React.ReactNode;
    color: string;
    available: boolean;
    route?: string;
}

const AcheterCredit: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { userData, refreshUserData } = useSession();

    const [mode, setMode] = useState<'forMe' | 'other'>('forMe');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState<Compte[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            if (!userData.accounts || userData.accounts.length === 0) {
                setAccounts([]);
                setLoading(false);
                return;
            }

            console.log('Raw accounts:', userData.accounts);

            // Filter accounts - Exclude SOL (by Type OR Code pattern 300...)
            const validAccounts = userData.accounts.filter((acc: any) => {
                const type = (acc.type || '').toString().toUpperCase();
                const code = (acc.code || '').toString();

                // Exclude if Type indicates SOL/Mutuelle
                if (type.includes('SOL') || type.includes('MUTUELLE') || type.includes('SÒL')) {
                    return false;
                }

                // Exclude if Code starts with 300 (Solidaire accounts)
                if (code.startsWith('300')) {
                    return false;
                }

                return true;
            });

            console.log('Filtered accounts (No SOL):', validAccounts);

            setAccounts(validAccounts);

            if (validAccounts.length > 0) {
                // Default to first account as requested
                setSelectedAccount(validAccounts[0]);
            } else {
                setSelectedAccount(null);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [userData.accounts]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const onRefresh = async () => {
        setRefreshing(true);
        await refreshUserData();
        setRefreshing(false);
    };

    const handleAccountChange = (account: Compte) => {
        setSelectedAccount(account);
        setShowAccountSelector(false);
    };

    const paymentMethods: PaymentMethod[] = [
        {
            id: 'zelle',
            name: 'Zelle',
            icon: <DollarSign className="w-4 h-4" />,
            color: '#6C1ED9',
            available: true,
            route: '/services/payment/zelle'
        },
        {
            id: 'stripe',
            name: 'Stripe',
            icon: <CreditCard className="w-4 h-4" />,
            color: '#6772E5',
            available: true,
            route: '/services/payment/stripe'
        },
        {
            id: 'paypal',
            name: 'PayPal',
            icon: <span className="text-sm font-bold">P</span>,
            color: '#0070BA',
            available: true,
            route: '/services/payment/paypal'
        },
        {
            id: 'moncash',
            name: 'MonCash',
            icon: <span className="text-xs font-bold">MC</span>,
            color: '#E53935',
            available: false,
            route: '/services/payment/moncash'
        },
        {
            id: 'natcash',
            name: 'NatCash',
            icon: <span className="text-xs font-bold">NC</span>,
            color: '#00897B',
            available: false,
            route: '/services/payment/natcash'
        }
    ];

    const handleContinue = () => {
        if (!amount || parseFloat(amount) <= 0) {
            alert(t('services:acheterCredit.errors.invalidAmount'));
            return;
        }
        if (mode === 'other' && !phoneNumber) {
            alert(t('services:acheterCredit.errors.requiredPhone'));
            return;
        }
        if (!selectedPaymentMethod) {
            alert(t('services:acheterCredit.errors.requiredMethod'));
            return;
        }
        if (!selectedAccount?.code) {
            alert(t('services:acheterCredit.errors.requiredAccount'));
            return;
        }

        const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
        if (method?.route) {
            navigate(method.route, {
                state: {
                    amount: amount,
                    accountCode: selectedAccount.code,
                    method: method.name,
                    color: method.color,
                    phoneNumber: mode === 'other' ? phoneNumber : undefined
                }
            });
        }
    };

    const formatBalance = (balance: number | undefined) => {
        return (Number(balance) || 0).toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const quickAmounts = [100, 250, 500, 1000];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 pb-40">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-xl border-b border-white/20 sticky top-0 z-20">
                <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-slate-700" />
                    </button>
                    <h1 className="text-lg font-bold text-slate-800">{t('services:acheterCredit.title')}</h1>
                    <button
                        onClick={onRefresh}
                        className={`w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors ${refreshing ? 'animate-spin' : ''}`}
                    >
                        <RefreshCw className="w-5 h-5 text-slate-700" />
                    </button>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 pt-4 space-y-4">
                {/* Balance Card */}
                <div className="relative">
                    <button
                        onClick={() => accounts.length > 1 && setShowAccountSelector(!showAccountSelector)}
                        className="w-full text-left"
                        disabled={accounts.length <= 1}
                    >
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 shadow-lg shadow-blue-600/20">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-white/80 text-sm">{t('services:acheterCredit.availableBalance')}</span>
                                {accounts.length > 1 && (
                                    <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${showAccountSelector ? 'rotate-180' : ''}`} />
                                )}
                            </div>
                            <div className="text-3xl font-bold text-white">
                                {formatBalance(selectedAccount?.solde_BS ?? selectedAccount?.soldeBS ?? selectedAccount?.solde)}
                                <span className="text-lg ml-2 font-medium">{selectedAccount?.devise || 'HTG'}</span>
                            </div>
                            <div className="mt-3">
                                <span className="inline-block bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-lg">
                                    {selectedAccount?.type || 'BS'} • {selectedAccount?.code?.slice(-4) || '0000'}
                                </span>
                            </div>
                        </div>
                    </button>

                    {/* Account Dropdown */}
                    {showAccountSelector && accounts.length > 1 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/30 overflow-hidden z-10">
                            {accounts.map((acc) => (
                                <button
                                    key={acc.code}
                                    onClick={() => handleAccountChange(acc)}
                                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors ${selectedAccount?.code === acc.code ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <span className="text-sm font-medium text-slate-700">
                                        {acc.type} • {acc.code}
                                    </span>
                                    <span className="text-sm font-bold text-blue-600">
                                        {formatBalance(acc.solde_BS ?? acc.soldeBS ?? acc.solde)} {acc.devise}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Toggle: For Me / For Other */}
                <div className="bg-white/60 backdrop-blur-xl rounded-xl p-1.5 border border-white/30 flex">
                    <button
                        onClick={() => setMode('forMe')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === 'forMe'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-blue-600 hover:bg-white/50'
                            }`}
                    >
                        <User className="w-4 h-4" />
                        {t('services:acheterCredit.forMe')}
                    </button>
                    <button
                        disabled
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-slate-400 cursor-not-allowed relative"
                    >
                        <Users className="w-4 h-4" />
                        {t('services:acheterCredit.forOthers')}
                        <span className="absolute -top-2 right-2 bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                            {t('services:acheterCredit.comingSoon')}
                        </span>
                    </button>
                </div>

                {/* Form Card */}
                <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-5 border border-white/30">
                    <h3 className="text-sm font-bold text-slate-800 mb-4">{t('services:acheterCredit.transactionDetails')}</h3>

                    {/* Phone number for "other" mode */}
                    {mode === 'other' && (
                        <div className="mb-4">
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                                {t('services:acheterCredit.phoneNumber')}
                            </label>
                            <div className="flex items-center bg-white/80 border border-slate-200 rounded-xl px-3 py-2.5">
                                <Phone className="w-4 h-4 text-slate-400 mr-2" />
                                <input
                                    type="tel"
                                    placeholder="+509 0000 0000"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Amount Input */}
                    <div className="mb-4">
                        <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                            {t('services:acheterCredit.amount')}
                        </label>
                        <div className="flex items-center bg-white/80 border border-slate-200 rounded-xl px-3 py-2.5">
                            <span className="text-lg font-bold text-slate-500">
                                {selectedAccount?.devise === 'USD' ? '$' : 'G'}
                            </span>
                            <input
                                type="number"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="flex-1 bg-transparent text-center text-lg font-semibold text-slate-700 outline-none"
                            />
                            <span className="text-sm font-medium text-slate-500">
                                {selectedAccount?.devise || 'HTG'}
                            </span>
                        </div>
                    </div>

                    {/* Quick Amounts */}
                    <div className="flex gap-2">
                        {quickAmounts.map((val) => (
                            <button
                                key={val}
                                onClick={() => setAmount(String(val))}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all border ${amount === String(val)
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white/60 text-slate-600 border-slate-200 hover:border-blue-400'
                                    }`}
                            >
                                {val}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-5 border border-white/30">
                    <h3 className="text-sm font-bold text-slate-800 mb-4">{t('services:acheterCredit.paymentMethod')}</h3>

                    <div className="space-y-2">
                        {paymentMethods.map((method) => (
                            <button
                                key={method.id}
                                onClick={() => method.available && setSelectedPaymentMethod(method.id)}
                                disabled={!method.available}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${selectedPaymentMethod === method.id
                                    ? 'bg-blue-50 ring-2 ring-blue-500'
                                    : method.available
                                        ? 'hover:bg-slate-50'
                                        : 'opacity-50 cursor-not-allowed'
                                    }`}
                            >
                                {/* Icon */}
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center border-2"
                                    style={{
                                        backgroundColor: `${method.color}15`,
                                        borderColor: `${method.color}40`,
                                        color: method.color
                                    }}
                                >
                                    {method.icon}
                                </div>

                                {/* Name */}
                                <span className="flex-1 text-left text-sm font-medium text-slate-700">
                                    {method.name}
                                    {!method.available && (
                                        <span className="ml-2 text-xs text-amber-500 font-bold">{t('services:acheterCredit.comingSoon')}</span>
                                    )}
                                </span>

                                {/* Radio */}
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === method.id
                                    ? 'border-blue-600'
                                    : 'border-slate-300'
                                    }`}>
                                    {selectedPaymentMethod === method.id && (
                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer Button - Higher z-index to appear above mobile navbar */}
            <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 p-4 z-50">
                <div className="max-w-lg mx-auto">
                    <button
                        onClick={handleContinue}
                        disabled={!amount || !selectedPaymentMethod}
                        className={`w-full py-3.5 rounded-xl text-white font-bold text-sm transition-all ${!amount || !selectedPaymentMethod
                            ? 'bg-slate-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30'
                            }`}
                    >
                        {t('services:acheterCredit.continue')}
                        {amount && (
                            <span className="ml-2">
                                • {formatBalance(parseFloat(amount))} {selectedAccount?.devise || 'HTG'}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcheterCredit;
